import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Button, Input, Textarea, textStyles, token } from '@bynder/design-system';
import { IconAdd, IconControlRightSymbol, IconDelete } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { type TranscriptCue } from '@bynder-studio/render-core';
import useOnClickOutside from 'packages/hooks/useOnClickOutside';
import { formatShortTimecode, parseShortTimecode, framesToSeconds } from './parseSrt';

const SHORT_TIMECODE_REGEX = /^\d{2}:\d{2}(,\d{1,3})?$/;

type TranscriptCueItemProps = {
    cue: TranscriptCue;
    index: number;
    frameRate: number;
    isEditing: boolean;
    onStartEdit: () => void;
    onEndEdit: () => void;
    onUpdate: (updatedCue: TranscriptCue) => void;
    onDelete: (id: string) => void;
    onInsertAfter: (id: string) => void;
};

const TranscriptCueItem = ({
    cue,
    index,
    frameRate,
    isEditing,
    onStartEdit,
    onEndEdit,
    onUpdate,
    onDelete,
    onInsertAfter,
}: TranscriptCueItemProps) => {
    const { translate } = useTranslate();
    const [isHovered, setIsHovered] = useState(false);
    const [editStart, setEditStart] = useState('');
    const [editEnd, setEditEnd] = useState('');
    const [localText, setLocalText] = useState(cue.text);
    const isTextFocusedRef = useRef(false);
    const cueContainerRef = useRef<HTMLDivElement>(null);
    const timestampRowRef = useRef<HTMLDivElement>(null);

    const shortStart = formatShortTimecode(framesToSeconds(cue.startFrame, frameRate));

    // Sync local text when an external update changes cue.text (e.g. generate)
    useEffect(() => {
        if (!isTextFocusedRef.current) {
            setLocalText(cue.text);
        }
    }, [cue.text]);

    // Populate timestamp inputs when entering edit mode
    useEffect(() => {
        if (!isEditing) {
            return;
        }

        setEditStart(formatShortTimecode(framesToSeconds(cue.startFrame, frameRate), true));
        setEditEnd(formatShortTimecode(framesToSeconds(cue.endFrame, frameRate), true));
    }, [isEditing, cue.startFrame, cue.endFrame, frameRate]);

    const enterEditMode = useCallback(() => {
        onStartEdit();
    }, [onStartEdit]);

    const commitTimestamps = useCallback(() => {
        const startSecs = SHORT_TIMECODE_REGEX.test(editStart)
            ? parseShortTimecode(editStart)
            : framesToSeconds(cue.startFrame, frameRate);
        const endSecs = SHORT_TIMECODE_REGEX.test(editEnd)
            ? parseShortTimecode(editEnd)
            : framesToSeconds(cue.endFrame, frameRate);
        const startFrame = Math.max(0, Math.round(startSecs * frameRate));
        const endFrame = Math.max(startFrame, Math.round(endSecs * frameRate));

        onUpdate({
            ...cue,
            startFrame,
            endFrame,
        });
        onEndEdit();
    }, [editStart, editEnd, cue, frameRate, onUpdate, onEndEdit]);

    const commitText = useCallback(() => {
        const trimmed = localText.trim();

        if (trimmed && trimmed !== cue.text) {
            onUpdate({ ...cue, text: trimmed });
        }
    }, [localText, cue, onUpdate]);

    const handleTimestampRowBlur = useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            if (!cueContainerRef.current?.contains(e.relatedTarget as Node)) {
                commitTimestamps();
            }
        },
        [commitTimestamps],
    );

    useOnClickOutside(cueContainerRef, () => {
        if (!isEditing) {
            return;
        }

        const rowNode = cueContainerRef.current;
        const activeElement = document.activeElement;

        // If one of timestamp inputs is focused, blur handler will commit.
        if (rowNode && activeElement && rowNode.contains(activeElement)) {
            return;
        }

        commitTimestamps();
    });

    const handleDelete = useCallback(() => {
        onDelete(cue.id);
    }, [cue.id, onDelete]);

    const handleInsertAfter = useCallback(() => {
        onInsertAfter(cue.id);
    }, [cue.id, onInsertAfter]);

    const handleTextareaKeyDown = useCallback((e: React.KeyboardEvent) => {
        // Keep caret navigation inside textarea from triggering parent shortcuts.
        if (e.key.startsWith('Arrow')) {
            e.stopPropagation();
        }
    }, []);

    return (
        <CueContainer ref={cueContainerRef}>
            {isEditing ? (
                <div ref={timestampRowRef} onBlur={handleTimestampRowBlur}>
                    <CueIndexLabel>{index}</CueIndexLabel>
                    <TimestampEditRow>
                        <TimeInput
                            value={editStart}
                            onChange={setEditStart}
                            aria-label={translate('editor.transcript.cue.start-time')}
                        />
                        <InactiveSeparatorButton variant="clean" icon={<IconControlRightSymbol />} aria-label="" />
                        <TimeInput
                            value={editEnd}
                            onChange={setEditEnd}
                            aria-label={translate('editor.transcript.cue.end-time')}
                        />
                        <Button
                            variant="clean"
                            icon={<IconDelete />}
                            onClick={handleDelete}
                            aria-label={translate('editor.transcript.cue.delete')}
                            title={translate('editor.transcript.cue.delete')}
                        />
                    </TimestampEditRow>
                </div>
            ) : (
                <CueHeader onDoubleClick={enterEditMode}>
                    <CueHeaderMeta>
                        {index} | {shortStart}
                    </CueHeaderMeta>
                </CueHeader>
            )}
            <StyledTextarea
                value={localText}
                onChange={setLocalText}
                onKeyDown={handleTextareaKeyDown}
                onFocus={() => {
                    isTextFocusedRef.current = true;
                }}
                onBlur={() => {
                    isTextFocusedRef.current = false;
                    commitText();
                }}
                aria-label={translate('editor.transcript.cue.text')}
            />
            <BottomHoverZone onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                {isHovered && (
                    <AddAfterButton
                        variant="clean"
                        isSmall
                        icon={<IconAdd />}
                        onClick={handleInsertAfter}
                        aria-label={translate('editor.transcript.cue.add-after')}
                        title={translate('editor.transcript.cue.add-after')}
                    />
                )}
            </BottomHoverZone>
        </CueContainer>
    );
};

const CueContainer = styled.div`
    position: relative;
    padding: ${token.spacing3};
    padding-bottom: 0;
    border-radius: 10px;
`;

const CueHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${token.spacing2};
    margin-bottom: ${token.spacing2};
    cursor: pointer;
    user-select: none;
`;

const CueIndexLabel = styled.span`
    ${textStyles.uiTextXS};
    color: ${token.gray500a};
`;

const CueHeaderMeta = styled.span`
    ${textStyles.uiTextXS};
    color: ${token.colorTextDisabled};
`;

const TimestampEditRow = styled.div`
    display: grid;
    gap: ${token.spacing2};
    margin-bottom: ${token.spacing2};
    grid-template-columns: 100px 40px 100px 40px;
`;

const TimeInput = styled(Input)`
    flex-shrink: 0;
`;

const InactiveSeparatorButton = styled(Button)`
    flex-shrink: 0;
    pointer-events: none;
`;

const StyledTextarea = styled(Textarea)`
    min-height: 72px;
`;

const AddAfterButton = styled(Button)`
    position: absolute;
    bottom: calc(50% - 10px);
    transform: translateY(50%);
    right: 0;
    z-index: 1;
`;

const BottomHoverZone = styled.div`
    position: relative;
    height: 16px;
    margin-top: ${token.spacing1};
`;

export default TranscriptCueItem;
